import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

function formatDate(dateStr) {
  return new Date(dateStr.replace(' ', 'T')).toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

export default function DeletionLog() {
  const [deletions,   setDeletions]   = useState([]);
  const [page,        setPage]        = useState(1);
  const [totalPages,  setTotalPages]  = useState(1);
  const [total,       setTotal]       = useState(0);
  const [loading,     setLoading]     = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error,       setError]       = useState('');

  const fetchPage = useCallback(async (pageNum, append) => {
    append ? setLoadingMore(true) : setLoading(true);
    setError('');
    try {
      const res = await api.get(`/gpf/v1/activity-log/deletions?page=${pageNum}`);
      setDeletions(prev => append ? [...prev, ...res.data.deletions] : res.data.deletions);
      setTotalPages(res.data.total_pages);
      setTotal(res.data.total);
    } catch (e) {
      setError('Could not load the deletion log.');
    } finally {
      append ? setLoadingMore(false) : setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPage(1, false); }, [fetchPage]);

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchPage(next, true);
  };

  if (loading) return <p style={styles.empty}>Loading deletion log...</p>;
  if (error) return <p style={{ ...styles.empty, color: '#dc2626' }}>{error}</p>;
  if (deletions.length === 0) return <p style={styles.empty}>No projects or tasks have been deleted. Nothing to show here — that's a good thing.</p>;

  return (
    <div>
      <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 16px' }}>
        {total} deletion{total !== 1 ? 's' : ''} recorded — every project or task ever deleted on this account, and who deleted it.
      </p>

      <div style={styles.table}>
        <div style={styles.headerRow}>
          <span style={{ flex: 2 }}>ITEM</span>
          <span style={{ flex: 1 }}>TYPE</span>
          <span style={{ flex: 1.5 }}>PART OF PROJECT</span>
          <span style={{ flex: 1.3 }}>DELETED BY</span>
          <span style={{ flex: 1.3 }}>WHEN</span>
        </div>
        {deletions.map(d => (
          <div key={d.id} style={styles.row}>
            <span style={{ flex: 2, fontWeight: 600, color: '#0f172a' }}>{d.item_title || '(untitled)'}</span>
            <span style={{ flex: 1 }}>
              <span style={{ ...styles.typeBadge, background: d.item_type === 'project' ? '#eff6ff' : '#f0fdf4', color: d.item_type === 'project' ? '#2563eb' : '#16a34a' }}>
                {d.item_type === 'project' ? '📁 Project' : '✅ Task'}
              </span>
            </span>
            <span style={{ flex: 1.5, color: '#64748b' }}>{d.item_type === 'task' ? d.project_title : '—'}</span>
            <span style={{ flex: 1.3, fontWeight: 600, color: '#dc2626' }}>{d.deleted_by}</span>
            <span style={{ flex: 1.3, color: '#94a3b8', fontSize: 12.5 }}>{formatDate(d.deleted_at)}</span>
          </div>
        ))}
      </div>

      {page < totalPages && (
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <button style={styles.loadMoreBtn} onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? 'Loading...' : `Show older deletions (${total - deletions.length} more)`}
          </button>
        </div>
      )}
    </div>
  );
}

const styles = {
  empty:       { fontSize: 13, color: '#94a3b8', textAlign: 'center', padding: '40px 0' },
  table:       { border: '1px solid #f1f5f9', borderRadius: 10, overflow: 'hidden' },
  headerRow:   { display: 'flex', padding: '10px 16px', background: '#f8fafc', fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.04em', borderBottom: '1px solid #f1f5f9' },
  row:         { display: 'flex', padding: '13px 16px', fontSize: 13, borderBottom: '1px solid #f8fafc', alignItems: 'center' },
  typeBadge:   { fontSize: 11.5, fontWeight: 600, padding: '3px 9px', borderRadius: 99 },
  loadMoreBtn: { padding: '8px 18px', border: '1.5px solid #e2e8f0', borderRadius: 8, background: 'white', fontSize: 13, cursor: 'pointer', color: '#374151' },
};